import { skipToken } from '@apollo/client/react'
import { uniq } from 'lodash-es'
import {
  forwardRef,
  type HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { FileTrigger as AriaFileTrigger } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'react-use'
import { Virtuoso, VirtuosoGrid } from 'react-virtuoso'
import { useImmer } from 'use-immer'
import FluentCheckmarkCircle32Filled from '~icons/fluent/checkmark-circle-32-filled'
import FluentDismiss24Filled from '~icons/fluent/dismiss-24-filled'
import FluentSearch24Filled from '~icons/fluent/search-24-filled'
import IcBaselineViewList from '~icons/ic/baseline-view-list'
import IcBaselineViewModule from '~icons/ic/baseline-view-module'
import MediaPreview from 'src/components/Common/MediaPreview'
import {
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
  UIDialogResponsive,
} from 'src/components/ui/Modal'
import type { TaskSummary } from 'src/services/apollo/generated/graphql'
import {
  useInfinityListTaskSummaries,
  useInfinityListUserTaskSummaries,
} from 'src/services/apollo/task'
import { useMe } from 'src/services/helpers/user'
import { cx } from 'src/utils/cx'
import dayjs from 'src/utils/dayjs'
import { getNodesFromConnection } from 'src/utils/graphql'
import { getImageThumbnailUrl } from 'src/utils/media'
import { getMediaIdsFromTaskSummary, isVideoTaskSummary } from 'src/utils/task'
import { useMediaUpload } from 'src/utils/useMediaUpload'
import { UIButton } from '../ui/Button'
import { UIMenu, UIMenuItem, UIMenuTrigger } from '../ui/Menu'
import Text from '../ui/Text'

type ViewMode = 'task' | 'image'

/**
 * Restricts which task outputs are shown in the picker:
 * - 'any' (default): still images and video tasks are both included
 * - 'still': exclude video tasks
 * - 'video': only video tasks
 */
export type TaskMediaPickerKind = 'any' | 'still' | 'video'

const PAGE_SIZE = 30

type TaskMediaPickerBaseProps = {
  open: boolean
  onClose?: () => void
  showFileUpload?: boolean
  acceptedMimeTypes?: string[]
  onSelectedFiles?: (files: File[]) => void
  mediaKind?: TaskMediaPickerKind
  /** Custom label for the bottom upload button. Defaults to "Upload image". */
  uploadLabel?: React.ReactNode
}

export type TaskMediaPickerProps = TaskMediaPickerBaseProps &
  (
    | {
        multiple?: false
        onSubmit?: (value: string) => void
        defaultValue?: string
      }
    | {
        multiple: true
        onSubmit?: (value: string[]) => void
        defaultValue?: string[]
      }
  )

export function TaskMediaPicker(props: TaskMediaPickerProps) {
  const { t } = useTranslation(['action', 'generation', 'common'])
  const [selected, updateSelected] = useImmer(() => new Set<string>())
  const [viewMode, setViewMode] = useState<ViewMode>('task')
  const [searchInput, setSearchInput] = useState('')
  const [debouncedKeyword, setDebouncedKeyword] = useState('')
  useDebounce(() => setDebouncedKeyword(searchInput), 300, [searchInput])

  const submitSingle = useCallback(
    (mediaId: string) => {
      if (!props.multiple) {
        props.onSubmit?.(mediaId)
      }
      props.onClose?.()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.multiple, props.onSubmit, props.onClose],
  )

  const onSelect = useCallback(
    (mediaId: string) => {
      if (props.multiple) {
        updateSelected(selected => {
          if (selected.has(mediaId)) {
            selected.delete(mediaId)
          } else {
            selected.add(mediaId)
          }
        })
      } else {
        submitSingle(mediaId)
      }
    },
    [props.multiple, updateSelected, submitSingle],
  )

  const handleConfirm = useCallback(() => {
    if (!props.multiple) return
    props.onSubmit?.(Array.from(selected))
    props.onClose?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.multiple, props.onSubmit, props.onClose, selected])

  const handleFiles = useCallback(
    (files: File[]) => {
      props.onSelectedFiles?.(files)
      props.onClose?.()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onSelectedFiles, props.onClose],
  )

  const upload = useMediaUpload({
    acceptedMimeTypes: props.acceptedMimeTypes,
    multiple: props.multiple,
    onMediaId: onSelect,
  })

  useEffect(() => {
    if (props.open && props.defaultValue) {
      updateSelected(new Set([props.defaultValue].flat()))
    } else if (!props.open) {
      updateSelected(new Set())
      setSearchInput('')
      setDebouncedKeyword('')
    }
  }, [props.defaultValue, props.open, updateSelected])

  return (
    <UIDialogResponsive
      open={props.open}
      onClose={props.onClose}
      className="flex flex-col mmd:h-[calc(var(--visible-height)-80px)]"
      dismissable
    >
      <UIDialogHeader
        className="pb-3 msm:pb-5"
        heading={
          <span className="capitalize">
            <Text i18nKey="generation:media-picker.dialog.title" />
          </span>
        }
      />
      <UIDialogContent className="flex-1 flex flex-col overflow-hidden">
        {/* Search bar */}
        <div className="flex-none mt-1 mb-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-black/5 dark:bg-white/5 ring-1 ring-black/10 dark:ring-white/10 focus-within:ring-purple-500 transition-shadow">
          <FluentSearch24Filled className="size-4 text-neutral-400 shrink-0" />
          <input
            type="text"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-neutral-400"
            placeholder={t('common:search')}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button
              type="button"
              aria-label={t('generation:image-edit.clear')}
              className="shrink-0 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
              onClick={() => setSearchInput('')}
            >
              <FluentDismiss24Filled className="size-3.5" />
            </button>
          )}
        </div>

        {/* View mode toggle */}
        <div className="flex-none flex items-center gap-1 pb-3">
          <button
            type="button"
            className={cx(
              'flex items-center gap-1 px-2.5 py-1.5 rounded-md text-sm transition',
              viewMode === 'task'
                ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100'
                : 'text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800',
            )}
            onClick={() => setViewMode('task')}
          >
            <IcBaselineViewList className="size-4" />
            <Text i18nKey="generation:media-picker.view-mode.tasks" />
          </button>
          <button
            type="button"
            className={cx(
              'flex items-center gap-1 px-2.5 py-1.5 rounded-md text-sm transition',
              viewMode === 'image'
                ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100'
                : 'text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800',
            )}
            onClick={() => setViewMode('image')}
          >
            <IcBaselineViewModule className="size-4" />
            <Text i18nKey="generation:media-picker.view-mode.images" />
          </button>
        </div>

        <section className="relative flex-1 min-h-0">
          {viewMode === 'task' ? (
            <TaskGroupedGrid
              keyword={debouncedKeyword}
              selected={selected}
              onSelect={onSelect}
              mediaKind={props.mediaKind}
              // 120px * 6 + 4px * 5 + 16px (scrollbar)
              className="h-full mmd:w-[756px]"
            />
          ) : (
            <FlatImageGrid
              keyword={debouncedKeyword}
              selected={selected}
              onSelect={onSelect}
              mediaKind={props.mediaKind}
              // 120px * 6 + 4px * 5 + 16px (scrollbar)
              className="h-full mmd:w-[756px]"
            />
          )}
        </section>

        {/* Multiple selection preview strip */}
        {props.multiple && selected.size > 0 && (
          <section className="flex-none mt-4 flex gap-2 h-12 overflow-x-auto">
            <button
              type="button"
              className="group sticky left-0 grid place-content-center h-full aspect-square bg-background-light rounded"
              onClick={() => {
                updateSelected(selected => {
                  selected.clear()
                })
              }}
            >
              <div className="contents group-hover:hidden">
                <span>{selected.size}</span>
              </div>
              <FluentDismiss24Filled className="size-6 hidden group-hover:block" />
            </button>
            {Array.from(selected).map(id => (
              <button
                type="button"
                key={id}
                className="group flex-none relative h-full aspect-square overflow-hidden rounded"
                onClick={() => {
                  updateSelected(selected => {
                    selected.delete(id)
                  })
                }}
              >
                <MediaPreview
                  mediaId={id}
                  thumbnail
                  preferStill
                  className="size-full"
                />
                <div className="absolute inset-0 grid place-content-center bg-black/50 invisible group-hover:visible">
                  <FluentDismiss24Filled className="size-6 text-white" />
                </div>
              </button>
            ))}
          </section>
        )}
      </UIDialogContent>

      <UIDialogFooter className="flex gap-2 msm:justify-start">
        {props.showFileUpload && (
          <>
            {upload.hasMultipleUploadSources ? (
              <UIMenu>
                <UIMenuTrigger>
                  <UIButton
                    variant="outlined"
                    className="capitalize"
                    busy={upload.uploading}
                  >
                    <Text i18nKey="generation:media-picker.select-button" />
                  </UIButton>
                </UIMenuTrigger>
                <UIMenuItem onAction={upload.openNativeGallery}>
                  <Text i18nKey="action:image-source.from-native-photo-gallery" />
                </UIMenuItem>
                <UIMenuItem onAction={upload.openNativeCamera}>
                  <Text i18nKey="action:image-source.from-native-camera" />
                </UIMenuItem>
              </UIMenu>
            ) : (
              <AriaFileTrigger
                acceptedFileTypes={upload.acceptedMimeTypes}
                allowsMultiple={props.multiple ?? false}
                onSelect={files => {
                  if (!files) return
                  const fileArray = [...files]
                  if (props.onSelectedFiles) {
                    handleFiles(fileArray)
                  } else {
                    upload.performUpload(...fileArray)
                  }
                }}
              >
                <UIButton
                  variant="outlined"
                  className="capitalize"
                  busy={upload.uploading}
                >
                  {props.uploadLabel ?? (
                    <Text i18nKey="generation:image-edit.upload-image" />
                  )}
                </UIButton>
              </AriaFileTrigger>
            )}
          </>
        )}

        <div className="flex gap-2 ml-auto">
          <UIButton
            className="capitalize"
            variant="outlined"
            onPress={props.onClose}
          >
            <Text i18nKey="action:cancel" />
          </UIButton>
          {props.multiple && (
            <UIButton
              variant="filled"
              className="capitalize"
              onPress={handleConfirm}
            >
              <Text i18nKey="action:confirm" />
            </UIButton>
          )}
        </div>
      </UIDialogFooter>
    </UIDialogResponsive>
  )
}

// =====================
// Shared data hook
// =====================

/**
 * Switches between the lighter user-scoped query (no keyword) and the
 * search-capable root query (with keyword).
 * See: https://mewtant-inc.sg.larksuite.com/docx/TLn6d8aIqoqwevxewBqlf6JHghb
 */
function useTaskSummariesData(keyword: string) {
  const { me } = useMe()
  const needsSearch = keyword.length > 0
  const defaultResult = useInfinityListUserTaskSummaries(
    me && !needsSearch ? { userId: me.id, last: PAGE_SIZE } : skipToken,
  )
  const searchResult = useInfinityListTaskSummaries(
    me && needsSearch ? { userId: me.id, keyword, last: PAGE_SIZE } : skipToken,
  )
  return needsSearch ? searchResult : defaultResult
}

// =====================
// Task-grouped grid
// =====================

function TaskGroupedGrid(props: {
  keyword: string
  selected: Set<string>
  onSelect: (mediaId: string) => void
  mediaKind?: TaskMediaPickerKind
  className?: string
}) {
  const { data, loading, loadingMore, fetchMore } = useTaskSummariesData(
    props.keyword,
  )
  const loadingData = loading || loadingMore
  const havingMore = useMemo(
    () => loading || Boolean(data?.pageInfo.hasPreviousPage),
    [data, loading],
  )

  const { mediaKind } = props
  /**
   * TODO: `isVideoTaskSummary` does not yet classify reference-video tasks as
   * "video" because the GraphQL `TaskCharacteristics` enum lacks a
   * `REFERENCE_VIDEO` value (see `packages/pixai-server/schema/type.graphql`).
   * Until the backend adds it and surfaces it on TaskSummary, ref-video task
   * outputs may be mis-bucketed here when callers pass `mediaKind`.
   */
  const tasks = useMemo(() => {
    const nodes = getNodesFromConnection(data) as TaskSummary[]
    return [...nodes].reverse().filter(t => {
      if (mediaKind === 'video' && !isVideoTaskSummary(t)) return false
      if (mediaKind === 'still' && isVideoTaskSummary(t)) return false
      return getMediaIdsFromTaskSummary(t).length > 0
    })
  }, [data, mediaKind])

  const handleEndReached = useCallback(() => {
    if (!loadingData && havingMore) {
      fetchMore({ variables: { before: data?.pageInfo?.startCursor } })
    }
  }, [loadingData, havingMore, fetchMore, data])

  return (
    <div className={props.className}>
      <Virtuoso
        data={tasks}
        endReached={handleEndReached}
        overscan={200}
        itemContent={(_, task) => (
          <TaskRow
            task={task}
            selected={props.selected}
            onSelect={props.onSelect}
          />
        )}
        components={{
          Footer: () => (
            <div className="py-4 text-sm text-center text-neutral-500">
              {loadingData ? (
                <Text i18nKey="common:hint.loading" />
              ) : tasks.length === 0 ? (
                <Text i18nKey="artwork:search-results.empty-without-keyword" />
              ) : !havingMore ? (
                <Text i18nKey="common:list-reached-the-end" />
              ) : null}
            </div>
          ),
        }}
        style={{ height: '100%' }}
      />
    </div>
  )
}

function NaturalMediaTile(props: {
  mediaId: string
  beingSelected: boolean
  onSelect: (mediaId: string) => void
}) {
  const { mediaId, beingSelected, onSelect } = props
  const [loaded, setLoaded] = useState(false)
  const url = getImageThumbnailUrl(mediaId)

  return (
    <button
      type="button"
      className={cx(
        'relative rounded-md overflow-hidden cursor-pointer transition ring-1 ring-black/10 dark:ring-white/10',
        !loaded && 'aspect-square bg-skeleton',
        beingSelected && 'scale-95 ring-2 ring-purple-600',
      )}
      onClick={() => onSelect(mediaId)}
    >
      <img
        src={url ?? undefined}
        loading="lazy"
        decoding="async"
        alt=""
        className={cx('w-full h-auto block', beingSelected && 'opacity-50')}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
      {beingSelected && (
        <FluentCheckmarkCircle32Filled className="absolute inset-0 m-auto size-8 text-purple-600 bg-black rounded-full" />
      )}
    </button>
  )
}

function TaskRow(props: {
  task: TaskSummary
  selected: Set<string>
  onSelect: (mediaId: string) => void
}) {
  const { task, selected, onSelect } = props
  const mediaIds = useMemo(() => getMediaIdsFromTaskSummary(task), [task])

  return (
    <div className="px-3 py-2">
      {/* Task header */}
      <div className="flex items-center gap-2 mb-1.5 text-xs text-neutral-500">
        <span>{dayjs(task.createdAt).fromNow()}</span>
        {mediaIds.length > 1 && (
          <span className="px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400">
            {mediaIds.length}
          </span>
        )}
      </div>
      {/* Media tiles */}
      <div className="grid grid-cols-3 mmd:grid-cols-4 gap-1.5">
        {mediaIds.map(mediaId => (
          <NaturalMediaTile
            key={mediaId}
            mediaId={mediaId}
            beingSelected={selected.has(mediaId)}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  )
}

// =====================
// Flat image grid
// =====================

const FlatImageGridList = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ style, children, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    style={style}
    className="grid grid-cols-3 mmd:grid-cols-4 gap-1.5 px-3 py-2"
  >
    {children}
  </div>
))
FlatImageGridList.displayName = 'FlatImageGridList'

function FlatImageGrid(props: {
  keyword: string
  selected: Set<string>
  onSelect: (mediaId: string) => void
  mediaKind?: TaskMediaPickerKind
  className?: string
}) {
  const { data, loading, loadingMore, fetchMore } = useTaskSummariesData(
    props.keyword,
  )

  const loadingData = loading || loadingMore
  const havingMore = useMemo(
    () => loading || Boolean(data?.pageInfo.hasPreviousPage),
    [data, loading],
  )

  const { mediaKind } = props
  /** Same caveat as in `TaskGroupedGrid`: ref-video classification pending backend enum. */
  const mediaIds = useMemo(() => {
    return uniq(
      (getNodesFromConnection(data) as TaskSummary[])
        .reverse()
        .filter(task => {
          if (mediaKind === 'video' && !isVideoTaskSummary(task)) return false
          if (mediaKind === 'still' && isVideoTaskSummary(task)) return false
          return true
        })
        .flatMap(task => getMediaIdsFromTaskSummary(task)),
    )
  }, [data, mediaKind])

  const handleEndReached = useCallback(() => {
    if (!loadingData && havingMore) {
      fetchMore({ variables: { before: data?.pageInfo?.startCursor } })
    }
  }, [loadingData, havingMore, fetchMore, data])

  return (
    <VirtuosoGrid
      data={mediaIds}
      endReached={handleEndReached}
      overscan={200}
      components={{
        List: FlatImageGridList,
        Footer: () => (
          <div className="py-4 text-sm text-center text-neutral-500">
            {loadingData ? (
              <Text i18nKey="common:hint.loading" />
            ) : mediaIds.length === 0 ? (
              <Text i18nKey="artwork:search-results.empty-without-keyword" />
            ) : !havingMore ? (
              <Text i18nKey="common:list-reached-the-end" />
            ) : null}
          </div>
        ),
      }}
      itemContent={(_, mediaId) => {
        const beingSelected = props.selected.has(mediaId)
        return (
          <div
            className={cx(
              'relative aspect-square rounded-md overflow-hidden cursor-pointer transition ring-1 ring-black/10 dark:ring-white/10',
              beingSelected && 'scale-95 ring-2 ring-purple-600',
            )}
            onClick={() => props.onSelect(mediaId)}
          >
            <MediaPreview
              mediaId={mediaId}
              thumbnail
              preferStill
              className={cx(
                'w-full h-full object-cover',
                beingSelected && 'opacity-50',
              )}
            />
            {beingSelected && (
              <FluentCheckmarkCircle32Filled className="absolute inset-0 m-auto size-8 text-purple-600 bg-black rounded-full" />
            )}
          </div>
        )
      }}
      className={props.className}
    />
  )
}
